import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import { When } from 'react-if';
import { IInvalidField } from 'shared';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { countries, CountryInterface } from 'country-codes-flags-phone-codes';

import { classNames }  from '../lib/classNames';

import { FormLabel } from './FormLabel';
import { FormDescription } from './FormDescription';
import { Paragraph } from './Typography/Paragraph';

/** Shown at the top of the list before the user searches */
const popularCountryCodes = [`AU`, `NZ`, `US`, `CA`, `SG`, `GB`, `ZA`];

/** Browser timezone -> country, for the popular countries only. Anything else falls back to the browser locale. */
const timezoneCountries: [RegExp, string][] = [
  [/^Australia\//, `AU`],
  [/^Pacific\/(Auckland|Chatham)$/, `NZ`],
  [/^America\/(New_York|Detroit|Chicago|Denver|Phoenix|Los_Angeles|Anchorage|Boise|Indiana|Kentucky)/, `US`],
  [/^Pacific\/Honolulu$/, `US`],
  [/^America\/(Toronto|Montreal|Vancouver|Edmonton|Calgary|Winnipeg|Regina|Halifax|Moncton|St_Johns|Whitehorse|Yellowknife|Iqaluit)$/, `CA`],
  [/^Asia\/Singapore$/, `SG`],
  [/^Europe\/(London|Belfast)$/, `GB`],
  [/^Africa\/Johannesburg$/, `ZA`],
];

/** Best-effort guess of the user's country from the browser, without a permission prompt */
function guessCountry(): CountryInterface | null {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ``;
  const fromTimezone = timezoneCountries.find(([pattern]) => pattern.test(timezone))?.[1];
  const code = fromTimezone || localeRegion();

  return countries.find(c => c.code === code) || null;
}

/** Region subtag of the browser locale, e.g. "SG" from "zh-Hans-SG". Null when malformed or missing. */
function localeRegion(): string | null {
  try {
    return new Intl.Locale(navigator.language).region?.toUpperCase() || null;
  }
  catch {
    return null;
  }
}

const allCountries = [...countries].sort((a, b) => a.name.localeCompare(b.name));

/** Popular countries, alphabetical. Shown above a divider; they also appear in the full list below it. */
const popularCountries = allCountries.filter(c => popularCountryCodes.includes(c.code));

/** Best-effort split of an existing value like "+64 21 123 456" into country + local number */
function splitPhoneNumber(value?: string): { country: CountryInterface | null; number: string } {
  if (!value?.startsWith(`+`)) {
    return { country: null, number: value || `` };
  }

  const country = countries
    .filter(c => value.startsWith(c.dialCode))
    .sort((a, b) => b.dialCode.length - a.dialCode.length)[0] || null;

  if (!country) {
    return { country: null, number: value };
  }

  return { country, number: value.slice(country.dialCode.length).trim() };
}

interface IPhoneNumberInputProps {
  /** Full phone number including dial code, e.g. "+64 21 123 456" */
  value?: string;
  onChange: (e: { value: string; name: string }) => void;
  name?: string;
  label?: string;
  subHeading?: string;
  placeholder?: string;
  invalid?: IInvalidField;
  required?: boolean;
  className?: string;
}

export function PhoneNumberInput({
  value,
  onChange,
  name,
  label,
  subHeading,
  placeholder,
  invalid,
  required,
  className,
}: IPhoneNumberInputProps) {
  const [{ country, number }, setParts] = useState(() => value ? splitPhoneNumber(value) : { country: guessCountry(), number: `` });
  const [query, setQuery] = useState(``);

  // Re-derive the parts when the parent swaps in a new value (e.g. a saved number arriving after load).
  // Local edits round trip through onChange unchanged, so they match and are left alone
  useEffect(() => {
    const combined = country ? `${country.dialCode} ${number}`.trim() : number;

    if (!value && !number) return;

    if ((value || ``) !== combined) {
      setParts(value ? splitPhoneNumber(value) : { country: guessCountry(), number: `` });
    }
  }, [value]);

  const filteredCountries = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^\+/, ``);

    if (!q) return allCountries;

    return allCountries.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.code.toLowerCase() === q ||
      c.dialCode.replace(`+`, ``).startsWith(q),
    );
  }, [query]);

  function emitChange(nextCountry: CountryInterface | null, nextNumber: string) {
    setParts({ country: nextCountry, number: nextNumber });

    const combined = nextCountry ?
      `${nextCountry.dialCode} ${nextNumber}`.trim() :
      nextNumber;

    onChange({ value: combined, name });
  }

  function renderOption(c: CountryInterface, key: string) {
    return (
      <ComboboxOption
        key={ key }
        value={ c }
        className={ ({ focus }) =>
          classNames(
            `relative cursor-default select-none py-2 pl-3 pr-9`,
            focus ? `bg-paidniceGreen-600 text-white` : `text-gray-900`,
          )
        }
      >
        { ({ focus, selected }) => (
          <>
            <span className={ classNames(`block truncate`, selected && `font-semibold`) }>
              { `${c.flag} ${c.name} (${c.dialCode})` }
            </span>

            { selected && (
              <span
                className={ classNames(
                  `absolute inset-y-0 right-0 flex items-center pr-4`,
                  focus ? `text-white` : `text-paidniceGreen-600`,
                ) }
              >
                <CheckIcon className={ `h-5 w-5` }
                  aria-hidden={ `true` } />
              </span>
            ) }
          </>
        ) }
      </ComboboxOption>
    );
  }

  return (
    <div className={ className }>
      <When condition={ !!label }>
        <FormLabel name={ name }>
          { label }
        </FormLabel>
      </When>
      <When condition={ !!subHeading }>
        <FormDescription>
          { subHeading }
        </FormDescription>
      </When>
      <div className={ `mt-2 flex rounded-md shadow-xs` }>
        <Combobox
          value={ country }
          onChange={ selected => emitChange(selected, number) }
          by={ `code` }
        >
          <div className={ `relative` }>
            <ComboboxInput
              className={
                classNames(
                  `h-full w-32 rounded-l-md border border-r-0 bg-white py-2 pl-3 pr-7 focus:border-paidniceGreen-500 focus:outline-hidden focus:ring-1 focus:ring-paidniceGreen-500 sm:text-sm`,
                  invalid ? `border-red-600` : `border-gray-300`,
                )
              }
              displayValue={ (c: CountryInterface | null) => c ? `${c.flag} ${c.dialCode}` : `` }
              onChange={ e => setQuery(e.target.value) }
              onBlur={ () => setQuery(``) }
              placeholder={ `🌐 Search` }
              aria-label={ `Country code` }
            />
            <ComboboxButton className={ `absolute inset-y-0 right-0 flex items-center px-1.5 focus:outline-hidden` }>
              <ChevronUpDownIcon className={ `h-5 w-5 text-gray-400` }
                aria-hidden={ `true` } />
            </ComboboxButton>

            <When condition={ filteredCountries.length > 0 }>
              <ComboboxOptions className={ `absolute z-10 mt-1 max-h-60 w-80 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm` }>
                <When condition={ !query.trim() }>
                  { popularCountries.map(c => renderOption(c, `popular-${c.code}`)) }
                  <div className={ `my-1 border-t border-gray-200` } />
                </When>
                { filteredCountries.map(c => renderOption(c, c.code)) }
              </ComboboxOptions>
            </When>
          </div>
        </Combobox>
        <input
          type={ `tel` }
          value={ number }
          onChange={ e => emitChange(country, e.target.value) }
          name={ name }
          id={ name }
          required={ required }
          className={
            classNames(
              `block w-full min-w-0 flex-1 rounded-none rounded-r-md focus:border-paidniceGreen-500 focus:ring-paidniceGreen-500 sm:text-sm`,
              invalid ? `border-red-600` : `border-gray-300`,
            )
          }
          placeholder={ placeholder }
        />
      </div>
      <When condition={ !!invalid }>
        <Paragraph variant={ `error` }
          size={ `xs` }>
          { invalid?.message }
        </Paragraph>
      </When>
    </div>
  );
}
